import { parseCodeOwners } from '../components/utils.esm.js';
import dayjs from 'dayjs';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
class GitlabCIClient {
  constructor({
    discoveryApi,
    identityApi,
    codeOwnersPath,
    readmePath,
    gitlabAuthApi,
    gitlabInstance,
    useOAuth
  }) {
    __publicField(this, "discoveryApi");
    __publicField(this, "identityApi");
    __publicField(this, "gitlabAuthApi");
    __publicField(this, "useOAth");
    __publicField(this, "codeOwnersPath");
    __publicField(this, "gitlabInstance");
    __publicField(this, "readmePath");
    this.discoveryApi = discoveryApi;
    this.codeOwnersPath = codeOwnersPath || "CODEOWNERS";
    this.readmePath = readmePath || "README.md";
    this.gitlabInstance = gitlabInstance;
    this.identityApi = identityApi;
    this.gitlabAuthApi = gitlabAuthApi;
    this.useOAth = useOAuth != null ? useOAuth : false;
  }
  static setupAPI({
    discoveryApi,
    identityApi,
    codeOwnersPath,
    readmePath,
    gitlabAuthApi,
    useOAuth
  }) {
    return {
      build: (gitlabInstance) => new this({
        discoveryApi,
        identityApi,
        codeOwnersPath,
        readmePath,
        gitlabInstance,
        gitlabAuthApi,
        useOAuth
      })
    };
  }
  async callApi(path, query, APIkind = "rest", options = {}) {
    var _a;
    const apiUrl = `${await this.discoveryApi.getBaseUrl(
      "gitlab"
    )}/${APIkind}/${this.gitlabInstance}`;
    const token = (await this.identityApi.getCredentials()).token;
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    if (this.useOAth) {
      const oauthToken = await this.gitlabAuthApi.getAccessToken([
        "read_api"
      ]);
      headers["gitlab-authorization"] = `Bearer ${oauthToken}`;
    }
    options = {
      ...options,
      headers: {
        ...options == null ? void 0 : options.headers,
        ...headers
      }
    };
    const response = await fetch(
      `${apiUrl}${path ? `/${path}` : ""}?${new URLSearchParams(
        query
      ).toString()}`,
      options
    );
    if (response.status === 200) {
      if ((_a = response.headers.get("content-type")) == null ? void 0 : _a.includes("application/json")) {
        return await response.json();
      } else {
        return response.text();
      }
    }
    return void 0;
  }
  callGraphQLApi(query) {
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(query)
    };
    return this.callApi("", {}, "graphql", options);
  }
  async getPipelineSummary(projectID) {
    const [pipelineObjects, projectObj] = await Promise.all([
      this.callApi(
        "projects/" + projectID + "/pipelines",
        {}
      ),
      this.callApi("projects/" + projectID, {})
    ]);
    if (pipelineObjects && projectObj) {
      pipelineObjects.forEach((element) => {
        element.project_name = projectObj.name;
      });
    }
    return pipelineObjects || void 0;
  }
  async getIssuesSummary(projectId) {
    const [issuesObject, projectObj] = await Promise.all([
      this.callApi(`projects/${projectId}/issues`, {}),
      this.callApi("projects/" + projectId, {})
    ]);
    if (issuesObject && projectObj) {
      issuesObject.forEach((element) => {
        element.project_name = projectObj.name;
      });
    }
    return issuesObject || void 0;
  }
  async getProjectName(projectID) {
    const projectObj = await this.callApi(
      "projects/" + projectID,
      {}
    );
    return projectObj == null ? void 0 : projectObj.name;
  }
  //TODO: Merge with getUserDetail
  async getUserProfilesData(contributorsData) {
    return Promise.all(
      contributorsData.map(async (contributor) => {
        const userProfile = await this.callApi("users", {
          search: contributor.email,
          without_project_bots: "true"
        });
        const user = userProfile == null ? void 0 : userProfile.find(
          (v) => v.name === contributor.name
        );
        if (user) {
          return {
            ...contributor,
            ...user
          };
        }
        return contributor;
      })
    );
  }
  async getUserDetail(username) {
    var _a;
    if (username.startsWith("@")) {
      username = username.slice(1);
    }
    const userDetail = (_a = await this.callApi("users", {
      username
    })) == null ? void 0 : _a[0];
    if (!userDetail)
      throw new Error(`user ${username} does not exist`);
    return userDetail;
  }
  async getGroupDetail(name) {
    if (name.startsWith("@")) {
      name = name.slice(1);
    }
    const groupDetail = await this.callApi(
      `groups/${encodeURIComponent(name)}`,
      { with_projects: "false" }
    );
    if (!groupDetail)
      throw new Error(`group ${name} does not exist`);
    return groupDetail;
  }
  async getMergeRequestsSummary(projectID) {
    return this.callApi(
      "projects/" + projectID + "/merge_requests",
      {}
    );
  }
  async getMergeRequestsStatusSummary(projectID, count) {
    return this.callApi(
      "projects/" + projectID + "/merge_requests",
      { per_page: (count != null ? count : 20).toString(10) }
    );
  }
  async getContributorsSummary(projectID) {
    const contributorsData = await this.callApi("projects/" + projectID + "/repository/contributors", {
      sort: "desc"
    });
    const updatedContributorsData = await this.getUserProfilesData(
      contributorsData
    );
    return updatedContributorsData;
  }
  async getLanguagesSummary(projectID) {
    return this.callApi(
      "projects/" + projectID + "/languages",
      {}
    );
  }
  async getReleasesSummary(projectID) {
    return this.callApi(
      "projects/" + projectID + "/releases",
      {}
    );
  }
  async getProjectDetails(projectSlug) {
    if (!projectSlug)
      return void 0;
    return this.callApi(
      "projects/" + encodeURIComponent(projectSlug),
      {}
    );
  }
  async getProjectCoverage(projectSlug, projectDefaultBranch) {
    if (!projectSlug)
      return void 0;
    return this.callGraphQLApi({
      variables: {
        projectSlug,
        projectDefaultBranch,
        updatedAfter: dayjs().subtract(30, "days").format("YYYY-MM-DD")
      },
      query: (
        /* GraphQL */
        `
                query getProjectCoverage(
                    $projectSlug: ID!
                    $updatedAfter: Time
                    $projectDefaultBranch: String
                ) {
                    project(fullPath: $projectSlug) {
                        pipelines(
                            ref: $projectDefaultBranch
                            updatedAfter: $updatedAfter
                        ) {
                            nodes {
                                coverage
                                createdAt
                            }
                        }
                    }
                }
            `
      )
    });
  }
  async getCodeOwners(projectID, branch = "HEAD", filePath) {
    filePath = filePath || this.codeOwnersPath;
    if (filePath.startsWith("./"))
      filePath = filePath.slice(2);
    const codeOwnersStr = await this.callApi(
      `projects/${projectID}/repository/files/${encodeURIComponent(
        filePath
      )}/raw`,
      { ref: branch }
    );
    if (!codeOwnersStr) {
      throw Error(`Code owners file not found`);
    }
    const codeOwners = parseCodeOwners(codeOwnersStr);
    const uniqueOwners = [
      ...new Set(codeOwners.flatMap((owner) => owner.owners))
    ];
    const ownersSettledResult = await Promise.allSettled(
      uniqueOwners.map(async (owner) => {
        try {
          const ownerData = await this.getUserDetail(owner);
          return ownerData;
        } catch (error) {
          return this.getGroupDetail(owner);
        }
      })
    );
    const owners = ownersSettledResult.filter((result) => result.status === "fulfilled").map(
      (result) => result.value
    );
    return owners;
  }
  async getReadme(projectID, branch = "HEAD", filePath) {
    filePath = filePath || this.readmePath;
    if (filePath.startsWith("./"))
      filePath = filePath.slice(2);
    const readmeStr = await this.callApi(
      `projects/${projectID}/repository/files/${encodeURIComponent(
        filePath
      )}/raw`,
      { ref: branch }
    );
    if (!readmeStr) {
      throw Error(`README file not found`);
    }
    return readmeStr;
  }
  getContributorsLink(projectWebUrl, projectDefaultBranch) {
    return `${projectWebUrl}/-/graphs/${projectDefaultBranch}`;
  }
  getOwnersLink(projectWebUrl, projectDefaultBranch, codeOwnersPath) {
    return `${projectWebUrl}/-/blob/${projectDefaultBranch}/${codeOwnersPath || this.codeOwnersPath}`;
  }
}

export { GitlabCIClient };
//# sourceMappingURL=GitlabCIClient.esm.js.map
